import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ModalsContext = createContext(undefined);
const ModalsActionsContext = createContext(undefined);

export function ModalsProvider({ children }) {
  const [modals, setModals] = useState({});

  const open = useCallback((modalId, data) => {
    setModals((prev) => ({
      ...prev,
      [modalId]: { isOpen: true, data },
    }));
  }, []);

  const close = useCallback((modalId) => {
    setModals((prev) => ({
      ...prev,
      [modalId]: { isOpen: false, data: undefined },
    }));
  }, []);

  const toggle = useCallback((modalId, data) => {
    setModals((prev) => {
      const currentModal = prev[modalId];
      const isCurrentlyOpen = currentModal?.isOpen ?? false;

      return {
        ...prev,
        [modalId]: {
          isOpen: !isCurrentlyOpen,
          data: isCurrentlyOpen ? undefined : data,
        },
      };
    });
  }, []);

  const closeAll = useCallback(() => {
    setModals((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = { isOpen: false, data: undefined };
      });
      return newState;
    });
  }, []);

  const stateValue = useMemo(
    () => ({
      modals,
      openModals: Object.keys(modals).filter((key) => modals[key]?.isOpen),
    }),
    [modals],
  );

  const actionsValue = useMemo(
    () => ({
      open,
      close,
      toggle,
      closeAll,
    }),
    [open, close, toggle, closeAll],
  );

  return (
    <ModalsContext.Provider value={stateValue}>
      <ModalsActionsContext.Provider value={actionsValue}>{children}</ModalsActionsContext.Provider>
    </ModalsContext.Provider>
  );
}

export function useModalsState() {
  const context = useContext(ModalsContext);
  if (context === undefined) {
    throw new Error("useModalsState must be used within ModalsProvider");
  }
  return context;
}

export function useModalsActions() {
  const context = useContext(ModalsActionsContext);
  if (context === undefined) {
    throw new Error("useModalsActions must be used within ModalsProvider");
  }
  return context;
}

export function useModals() {
  const state = useModalsState();
  const actions = useModalsActions();

  const isOpen = useCallback(
    (modalId) => state.modals[modalId]?.isOpen ?? false,
    [state.modals]
  );

  const getData = useCallback(
    (modalId) => state.modals[modalId]?.data,
    [state.modals]
  );

  return { ...state, ...actions, isOpen, getData };
}
